import axios from 'axios';
import moment from 'moment';
import React, { FC, useState, useEffect } from 'react';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import { RouteComponentProps } from 'react-router-dom';
import { AnyAction } from 'redux';
import { ThunkDispatch } from 'redux-thunk';
import Swal from 'sweetalert2';
import { setAdditionalData } from '../../../reducers/additionalDataSlice';
import {
  skipYearbookSelectionAndGoToOrder,
  fetchYearbookPoses,
  YearbookPose,
} from '../../../actions/yearbook';
import type { ImageIntentContext } from '../../../utils/imageIntentReporter';
import useModalCarousel from '../hooks/modalCarousel';
import ModalCarousel from '../modalCarousel/ModalCarousel';
import {
  ImageProtectionOverlay,
  useImageIntentFriction,
} from '../shared/protectedImage';
import { attachImageIntentListeners } from '../shared/protectedImage/imageIntentGestures';
import {
  selectImageIntentGallery,
  selectImageProtectionStyle,
  selectYearbookPickerDp2Gallery,
  YearbookPickerState,
} from '../../../selectors';
import {
  AlertText,
  Header,
  PrimaryButton,
  RedItalicText,
  SecondaryButton,
  Text,
  Wrapper,
} from '../v3Styles';
import {
  CarouselImg,
  CarouselImgContainer,
  CarouselImgWrapper,
  CategoryWrapper,
  Grid,
  Image,
  ImageButton,
  ImageWrapper,
  protectionOverlayHtml,
  ZoomIcon,
} from './yearbookStyles';

export const SelectedBanner = styled.div`
  background: #0b76b7;
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 35px;
  line-height: 35px;
  text-align: center;
  color: #ffffff;
  font-family: 'Inter', sans-serif;
  z-index: 10;
`;

interface YearbookBackground {
  id: number;
  thumbnail_url?: string | null;
}

// The slices of the store this screen reads.
interface SelectYearbookPhotoState extends YearbookPickerState {
  shoot: {
    yearbookFixedBackground?: YearbookBackground | null;
    yearbookBackgroundSelectionType?: string | null;
    yearbookSelectionInstructions?: string | null;
    yearbookSelectionDeadline?: string | null;
  };
  yearbookPoses: {
    poses: YearbookPose[] | null;
    loading: boolean;
    error: string | null;
  };
}

type SelectYearbookPhotoProps = RouteComponentProps<{ studentId: string }>;

const SelectYearbookPhoto: FC<SelectYearbookPhotoProps> = ({
  history,
  match,
}) => {
  const dispatch = useDispatch<ThunkDispatch<unknown, unknown, AnyAction>>();
  const { studentId } = match.params;
  const [selectedImageId, setSelectedImageId] = useState<number | null>(null);
  const { open, closeModal, openModal, slideIndex, setSlideIndex } =
    useModalCarousel();
  const shoot = useSelector((state: SelectYearbookPhotoState) => state.shoot);
  // The DP2 gallery the parent opened this picker from, by key and id, when
  // known; both requests below name it so the server answers for that gallery
  // and not another school year's (ENG-3574). The selector keeps the same
  // object until the gallery changes, so the fetch below runs once per gallery.
  const dp2Gallery = useSelector((state: SelectYearbookPhotoState) =>
    selectYearbookPickerDp2Gallery(state, studentId),
  );
  const fixedBackground = shoot.yearbookFixedBackground;
  const isFixedBg = shoot.yearbookBackgroundSelectionType === 'fixed_selection';
  // The tiles report the order's gallery through the hook's Redux defaults.
  // The confirm dialog is raw HTML outside the hook, so it names the same
  // gallery itself.
  const { protectionEnabled, getFrictionProps } = useImageIntentFriction({
    surface: 'yearbook',
  });
  const dialogGallery = useSelector(selectImageIntentGallery);
  const protectionStyle = useSelector(selectImageProtectionStyle);

  // Distinct imageKey per pose: the reporter throttles on event type + image,
  // so poses must not share a bucket and suppress each other's signals.
  const poseContext = (image: YearbookPose): ImageIntentContext => ({
    imageKey: image.url,
    surface: 'yearbook',
    metadata: { image_id: image.id, kind: 'yearbook_pose' },
  });

  // Get poses from Redux state
  const { poses, loading, error } = useSelector(
    (state: SelectYearbookPhotoState) => state.yearbookPoses,
  );

  // Fetch poses data on component mount
  useEffect(() => {
    if (studentId) {
      dispatch(fetchYearbookPoses(studentId, dp2Gallery));
    }
  }, [dispatch, studentId, dp2Gallery]);

  const updateYearbookImage = async (
    imageId: number,
    yearbookBackgroundId: number | null = null,
  ) => {
    const body: {
      yearbook_selection: boolean;
      yearbook_background_id?: number;
    } = {
      yearbook_selection: true,
    };
    if (yearbookBackgroundId) {
      body.yearbook_background_id = yearbookBackgroundId;
    }
    const response = await axios.patch(
      `/api/v2/yearbook/images/${selectedImageId || imageId}`,
      body,
    );
    if (response.status >= 200 && response.status < 300) {
      history.push('/v3/yearbook-selection-complete');
    } else {
      throw new Error('Something went wrong');
    }
  };

  const showConfigurationError = () => {
    Swal.fire({
      title: 'Yearbook configuration unavailable',
      text: 'We could not load the yearbook background configuration. Return to the dashboard and open this gallery again.',
      icon: 'error',
      confirmButtonText: 'Return to dashboard',
      preConfirm: () => history.push('/dashboard'),
    });
  };

  const confirmPoseSelection = (
    image: YearbookPose,
    background: YearbookBackground | null = null,
  ) => {
    let detachDialogListeners: (() => void) | null = null;
    Swal.fire({
      html: `
          <p class="header">
            You have made your yearbook pose selection
          </p>
          <p class="red italic">
            ${
              shoot.yearbookSelectionInstructions
                ? shoot.yearbookSelectionInstructions
                : ''
            }
          </p>
          <p>
            If this is not the pose you wish to have in the yearbook, please go back and change your selection
          </p>
          <div class="img-wrapper" data-image-intent>
            <img class="bg" src="${background?.thumbnail_url || ''}"/>
            <img src="${image.url}"/>
            ${protectionEnabled ? protectionOverlayHtml(protectionStyle) : ''}
          </div>
        `,
      confirmButtonText: 'Select and Continue',
      showCancelButton: true,
      customClass: {
        popup: 'popup',
        confirmButton: 'confirm-button',
        cancelButton: 'cancel-button',
      },
      // The dialog's composite is raw HTML, outside React, so it gets the
      // same logging and blocking as the tiles through native listeners.
      didOpen: () => {
        const imgWrapper = document.querySelector('.swal2-popup .img-wrapper');
        if (imgWrapper) {
          detachDialogListeners = attachImageIntentListeners(imgWrapper, {
            ...poseContext(image),
            ...dialogGallery,
            surface: 'yearbook_confirm',
          });
        }
      },
      willClose: () => detachDialogListeners?.(),
      preConfirm: async () => {
        try {
          await updateYearbookImage(image.id, background?.id);
        } catch {
          Swal.showValidationMessage(
            'We could not save your yearbook selection. Please try again. If the problem continues, return to the dashboard and reload this gallery.',
          );
        }
      },
    });
  };

  const handleImageClick = (image: YearbookPose) => () => {
    dispatch(setAdditionalData({ currentGalleryImageId: image.id }));
    setSelectedImageId(image.id);
    const backgroundSelectionType = shoot.yearbookBackgroundSelectionType;

    if (backgroundSelectionType === 'custom_selection') {
      history.push('/v3/yearbook-background-selection');
    } else if (backgroundSelectionType === 'no_selection') {
      confirmPoseSelection(image);
    } else if (
      backgroundSelectionType === 'fixed_selection' &&
      fixedBackground?.id
    ) {
      confirmPoseSelection(image, fixedBackground);
    } else {
      showConfigurationError();
    }
  };

  const handleSkip = async () => {
    try {
      const url = `/api/v2/yearbook/skip-yearbook-selection/${studentId}`;
      // With no known gallery the POST stays bodiless, as it always was.
      const response = await (dp2Gallery
        ? axios.post(url, {
            dp2_gallery_id: dp2Gallery.id,
            dp2_gallery_key: dp2Gallery.key,
          })
        : axios.post(url));
      if (response.status >= 200 && response.status < 300) {
        // The store's last middleware (postConfigRoutingMiddleware) is async,
        // so at runtime dispatch returns a promise even for this plain action;
        // Promise.resolve adopts that promise as-is.
        Promise.resolve(dispatch(skipYearbookSelectionAndGoToOrder(true))).then(
          () => {
            history.push('/dashboard');
          },
        );
      } else {
        throw new Error('Something went wrong');
      }
    } catch (error) {
      console.error(error);
      throw new Error('Something went wrong');
    }
  };

  const handleSkipPress = () => {
    Swal.fire({
      html: `
        <p class="header">
          <span class="red">Please confirm:</span> you do not wish to select any of the photos from this gallery.
        </p>
      `,
      showDenyButton: true,
      denyButtonColor: '#0B76B7',
      denyButtonText: 'Nevermind, go back',
      confirmButtonText: 'Yes, bypass selection',
      customClass: {
        popup: 'popup',
        confirmButton: 'button--secondary',
        denyButton: 'deny-button',
      },
      preConfirm: handleSkip,
    });
  };

  const handleZoomClick = (index: number) => () => {
    setSlideIndex(index);
    openModal();
  };

  return (
    <Wrapper>
      <Header>Yearbook Pose Selection</Header>
      <Text>
        Please select the photo you would like to be used as your photo in the
        yearbook
      </Text>
      <RedItalicText>{shoot.yearbookSelectionInstructions}</RedItalicText>
      <AlertText>{`Yearbook poses must be selected by ${moment(
        shoot.yearbookSelectionDeadline,
      ).format('MMMM DD, YYYY')}`}</AlertText>

      {loading && (
        <div style={{ textAlign: 'center', padding: '20px' }}>
          Loading yearbook poses...
        </div>
      )}

      {error && (
        <div style={{ textAlign: 'center', padding: '20px', color: 'red' }}>
          Error loading yearbook poses: {error}
        </div>
      )}

      {!loading && !error && poses && poses.length > 0 && (
        <CategoryWrapper>
          <Grid>
            {poses.map((image, index) => {
              const selected = selectedImageId === image.id;
              const className = image.selected ? 'selected' : 'not-selected';
              return (
                <div key={image.id}>
                  <ImageWrapper
                    onContextMenu={(event) => event.preventDefault()}
                    selected={selected}
                    className={className}
                  >
                    <>
                      <Image
                        {...getFrictionProps(poseContext(image))}
                        background={
                          isFixedBg
                            ? fixedBackground?.thumbnail_url || null
                            : null
                        }
                        src={image.url}
                      />
                      {image.selected && (
                        <SelectedBanner>✅ Selected Pose</SelectedBanner>
                      )}
                      <ImageProtectionOverlay
                        context={poseContext(image)}
                        imageSrc={image.url}
                      />
                    </>
                    {!image.selected && (
                      <>
                        <ZoomIcon onClick={handleZoomClick(index)} />
                        <ImageButton onClick={handleImageClick(image)}>
                          Select pose
                        </ImageButton>
                      </>
                    )}
                  </ImageWrapper>
                  <span
                    style={{
                      display: 'block',
                      textAlign: 'center',
                      fontWeight: 'bold',
                    }}
                  >
                    {image.filename}
                  </span>
                </div>
              );
            })}
          </Grid>
        </CategoryWrapper>
      )}

      {!loading && !error && (!poses || poses.length === 0) && (
        <div style={{ textAlign: 'center', padding: '20px' }}>
          No yearbook poses available.
        </div>
      )}

      <p>Don&apos;t want to select any of these photos?</p>
      <SecondaryButton onClick={handleSkipPress}>
        Skip Yearbook Pose Selection
      </SecondaryButton>

      {poses && poses.length > 0 && (
        <ModalCarousel
          open={open}
          onClose={closeModal}
          selectedItem={slideIndex}
        >
          {poses.map((image) => (
            <>
              <CarouselImgWrapper key={image.id}>
                <CarouselImgContainer>
                  <CarouselImg
                    {...getFrictionProps(poseContext(image))}
                    background={
                      isFixedBg ? fixedBackground?.thumbnail_url || null : null
                    }
                    src={image.url}
                  />
                  <ImageProtectionOverlay
                    context={poseContext(image)}
                    imageSrc={image.url}
                  />
                </CarouselImgContainer>
                <PrimaryButton onClick={handleImageClick(image)}>
                  Select pose
                </PrimaryButton>
              </CarouselImgWrapper>
            </>
          ))}
        </ModalCarousel>
      )}
    </Wrapper>
  );
};

export default SelectYearbookPhoto;
